/**
 * Notes view - CRUD per date with color customization.
 */

import { useState, useEffect } from 'react';
import type { Note, CreateNoteInput } from '@anna-calendar/shared';
import { useNotesStore } from '../store/notes';
import { NoteModal } from '../components/NoteModal';
import { todayISO } from '../utils/dates';

export function NotesView() {
  const { notes, fetchNotes, createNote, updateNote, deleteNote } = useNotesStore();
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [selectedDate, setSelectedDate] = useState(todayISO());

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const filteredNotes = notes
    .filter((n) => n.date === selectedDate)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  // Dates that have notes (for the mini calendar)
  const noteDatesSet = new Set(notes.map((n) => n.date));

  const handleCreate = async (data: CreateNoteInput) => {
    await createNote(data);
  };

  const handleUpdate = async (data: CreateNoteInput) => {
    if (!editingNote) return;
    await updateNote(editingNote.id, data);
    setEditingNote(null);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this note?')) {
      await deleteNote(id);
    }
  };

  const openEdit = (note: Note) => {
    setEditingNote(note);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingNote(null);
  };

  // Generate last 30 days for the date selector
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return d.toISOString().slice(0, 10);
  });

  return (
    <div className="h-full overflow-y-auto p-4 desktop-pb">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Notes
        </h2>
        <button
          onClick={() => { setEditingNote(null); setShowModal(true); }}
          className="px-4 py-2 rounded-lg text-sm font-medium"
          style={{ backgroundColor: 'var(--accent)', color: '#fff', minHeight: '44px' }}
        >
          + New Note
        </button>
      </div>

      {/* Date selector */}
      <div className="mb-4 flex gap-2 items-center">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 rounded-lg text-base outline-none"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            minHeight: '44px',
          }}
        />
        <button
          onClick={() => setSelectedDate(todayISO())}
          className="px-3 py-2 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: selectedDate === todayISO() ? 'var(--accent-soft)' : 'var(--bg-tertiary)',
            color: selectedDate === todayISO() ? 'var(--accent)' : 'var(--text-secondary)',
            minHeight: '44px',
          }}
        >
          Today
        </button>
      </div>

      {/* Date chips with note indicators */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-2">
        {last30Days.map((date) => {
          const day = new Date(date + 'T12:00:00');
          const dayNum = day.getDate();
          const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short' });
          const hasNotes = noteDatesSet.has(date);
          const isSelected = date === selectedDate;

          return (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors flex-shrink-0"
              style={{
                backgroundColor: isSelected ? 'var(--accent)' : 'var(--bg-tertiary)',
                color: isSelected ? '#fff' : 'var(--text-secondary)',
                minWidth: '44px',
                minHeight: '44px',
              }}
            >
              <span className="text-[10px]">{dayLabel}</span>
              <span className="text-sm font-semibold">{dayNum}</span>
              {hasNotes && !isSelected && (
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--warning)' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Notes list */}
      {filteredNotes.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
          No notes for {selectedDate === todayISO() ? 'today' : selectedDate}
        </p>
      ) : (
        <div className="space-y-3">
          {filteredNotes.map((note) => (
            <div
              key={note.id}
              className="p-4 rounded-lg group"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                borderLeft: `4px solid ${note.color}`,
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0" onClick={() => openEdit(note)} style={{ cursor: 'pointer' }}>
                  <p className="text-base whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
                    {note.content}
                  </p>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                    {new Date(note.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(note)}
                    className="p-2 rounded-lg"
                    style={{ color: 'var(--text-muted)', minHeight: '44px', minWidth: '44px' }}
                    aria-label="Edit note"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="p-2 rounded-lg"
                    style={{ color: 'var(--danger)', minHeight: '44px', minWidth: '44px' }}
                    aria-label="Delete note"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Color indicator dot */}
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: note.color }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Note color</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Note modal */}
      <NoteModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={editingNote ? handleUpdate : handleCreate}
        note={editingNote}
        defaultDate={selectedDate}
      />
    </div>
  );
}
